import {
  computed,
  inject,
  Injectable,
  Signal,
  signal,
  WritableSignal,
} from "@angular/core";
import { AvailabilityMap } from "../../../interfaces/google-calendar-availability.interface";
import { GoogleCalendarAvailabilityApiService } from "../../api/calendar/google-calendar-availability-api.service";
import { ResponseAvailabilityDto } from "../../../api/models";

@Injectable({
  providedIn: "root",
})
export class GoogleCalendarAvailabilityCacheService {
  private readonly _googleCalendarApiService: GoogleCalendarAvailabilityApiService =
    inject(GoogleCalendarAvailabilityApiService);

  // Cache storage using signals
  private readonly _availabilityCache: WritableSignal<AvailabilityMap | null> =
    signal<AvailabilityMap | null>(null);

  private readonly _email: WritableSignal<string | null> = signal<
    string | null
  >(null);

  // Computed signals for consumers
  public readonly availabilityMap: Signal<AvailabilityMap> = computed(
    () => this._availabilityCache() || {},
  );

  // Computed signal for disabled dates
  public readonly disabledDates: Signal<Date[]> = computed(() => {
    const availabilityMap = this._availabilityCache();
    if (!availabilityMap) return [];

    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const allDates: Date[] = [];

    // Generate all dates for the next 3 months
    for (let month = 0; month < 3; month++) {
      const targetMonth = (currentMonth + month) % 12;
      const targetYear = currentYear + Math.floor((currentMonth + month) / 12);
      const daysInMonth = new Date(targetYear, targetMonth + 1, 0).getDate();

      for (let day = 1; day <= daysInMonth; day++) {
        allDates.push(new Date(targetYear, targetMonth, day));
      }
    }

    // Filter dates that are not in the availability map
    return allDates.filter((date) => {
      const dateStr = this._formatDateKey(date);
      return !availabilityMap[dateStr] || availabilityMap[dateStr].length === 0;
    });
  });

  // Signal for selected date's available hours
  private readonly _selectedDate: WritableSignal<Date | null> =
    signal<Date | null>(null);

  // Computed signal for available hours on selected date
  public readonly availableHours: Signal<string[]> = computed(() => {
    const date = this._selectedDate();
    const availabilityMap = this._availabilityCache();

    if (!date || !availabilityMap) return [];

    const dateKey = this._formatDateKey(date);
    const timeStrings = availabilityMap[dateKey] || [];

    return timeStrings;
  });

  public readonly firstAvailableDate: Signal<string | null> = computed(() => {
    const availabilityMap = this._availabilityCache();
    if (!availabilityMap) return "";

    const sortedDates = Object.keys(availabilityMap)
      .filter((dateStr) => availabilityMap[dateStr].length > 0)
      .sort(); // le date in formato ISO vengono ordinate correttamente come stringhe

    return sortedDates.length > 0 ? sortedDates[0] : null;
  });

  /**
   * Sets the email and fetches availability if not cached
   * @param email The email to use for the request
   */
  public setEmail(email: string): void {
    if (!email) {
      return;
    }

    // Only fetch new data if email changes
    if (this._email() !== email) {
      this._email.set(email);
      this._fetchAvailability();
    }
  }

  /**
   * Sets the selected date to get available hours
   * @param date The date to get available hours for
   */
  public setSelectedDate(date: Date): void {
    this._selectedDate.set(date);
  }

  /**
   * Refreshes the availability data
   */
  public refresh(): void {
    this._fetchAvailability();
  }

  /**
   * Resets the cache and email
   */
  public reset(): void {
    this._email.set(null);
    this._availabilityCache.set(null);
    this._selectedDate.set(null);
  }

  /**
   * Fetch availability data from API
   */
  private _fetchAvailability(): void {
    const email = this._email();
    if (!email) {
      return;
    }

    this._googleCalendarApiService.getAvailability$(email).subscribe({
      next: (response: ResponseAvailabilityDto) => {
        this._availabilityCache.set(response.availability);
      },
    });
  }

  /**
   * Formats a date to the expected key format in the availability map
   * @param date The date to format
   * @returns The formatted date string
   */
  private _formatDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  /**
   * Check if a date is disabled
   * @param date The date to check
   * @returns Whether the date is disabled
   */
  public isDateDisabled = (date: Date): boolean => {
    const disabledDates = this.disabledDates();
    return disabledDates.some(
      (d) =>
        d.getFullYear() === date.getFullYear() &&
        d.getMonth() === date.getMonth() &&
        d.getDate() === date.getDate(),
    );
  };
}
